import React from 'react'
import { Map } from 'immutable'
import { Image } from './image'
import { defaultSrc, ImageSize, pageHeaderLogoSrc, pageHeaderLogoSrcSet } from './imageSizes'

// ----- Departments

export const getDepartmentIconId = (department: SpaarneDepartmentType): SpaarneIconType => {
  // Icon 'zoeken' is the fallback for a department without an icon
  switch (department) {
    case 'Algemene opname afdeling (AOA)':
      return 'aoa'
    case 'Anesthesiologie':
      return 'anesthesiologie'
    case 'Apotheek':
      return 'apotheek'
    case 'Baria Nederland (obesitas chirurgie)':
      return 'baria-nederland'
    case 'Bekkenbodem centrum':
      return 'urologie'
    case 'Bloedafname (Atalmedial)':
      return 'bloedafname'
    case 'Cardiologie':
      return 'cardiologie'
    case 'Centrum Seksueel Geweld':
      return 'zoeken'
    case 'Chirurgie':
      return 'chirurgie'
    case 'Dermatologie':
      return 'dermatologie'
    case 'Dialyse':
      return 'dialyse'
    case 'Diëtetiek':
      return 'dietetiek'
    case 'Ergotherapie':
    case 'Fysiotherapie':
      return 'paramedische-dienst'
    case 'Geriatrie':
      return 'geriatrie'
    case 'Eerste hartlonghulp':
      return 'eerste-hartlonghulp'
    case 'Gipspolikliniek':
    case 'Spoedeisende Hulp (SEH)':
      return 'spoedeisende-hulp'
    case 'Gynaecologie':
    case 'Verloskunde':
      return 'gynaecologie'
    case 'Logopedie':
      return 'paramedische-dienst'
    case 'Geestelijke verzorging':
      return 'geest-verzorging'
    case 'Intensive care':
      return 'intensive-care'
    case 'Infectiepreventie':
      return 'zoeken'
    case 'Interne geneeskunde':
      return 'interne-geneeskunde'
    case 'Keel-, neus- en oorheelkunde (KNO)':
      return 'kno'
    case 'Kindergeneeskunde':
      return 'kindgeneeskunde'
    case 'Liesbreukpolikliniek':
    case 'Vaatlaboratorium':
    case 'Wonddiagnosecentrum':
      return 'chirurgie'
    case 'Longgeneeskunde':
      return 'longziekten'
    case 'Mondziekten, Kaak- en Aangezichtschirurgie (MKA-chirurgie)':
      return 'mond-kaak-en-aangezicht'
    case 'Maag-, darm- en leverziekten (MDL)':
      return 'maag-darm'
    case 'Maatschappelijk werk':
      return 'maatschappelijk-werk'
    case 'Neonatologie':
      return 'neonatologie'
    case 'Klinische Neurofysiologie (KNF)':
    case 'Neurochirurgie':
    case 'Neurologie':
      return 'neurologie'
    case 'Nucleaire geneeskunde':
      return 'nucleaire-geneeskunde'
    case 'Mammapoli':
    case 'Oncologie':
      return 'oncologie'
    case 'Oogheelkunde':
      return 'oogheelkunde'
    case 'Orthopedie':
      return 'orthopedie'
    case 'Pathologie':
      return 'pathologie'
    case 'Palliatieve zorg':
      return 'palliatieve-zorg'
    case 'Pijnbestrijding':
      return 'pijnbestrijding'
    case 'Plastische chirurgie':
      return 'plastische-chirurgie'
    case 'Psychiatrie en medische psychologie':
      return 'medische-psychologie'
    case 'Radiologie':
      return 'radiologie'
    case 'Reizigersvaccinatie':
      return 'reizigerspoli'
    case 'Reumatologie':
      return 'reumatologie'
    case 'Revalidatiegeneeskunde':
      return 'revalidatie'
    case 'Urologie':
      return 'urologie'
    case 'Vrouw en Kind':
      return 'vrouw-en-kind-centrum'
  }
}

export const DepartmentIcon = (p: {
  department: SpaarneDepartmentType
  width?: string | number
  height?: string | number
  className?: string
}) => (
  <svg aria-hidden="true" width={p.width || undefined} height={p.height || undefined} className={p.className}>
    <use xlinkHref={`#icon--SG-${getDepartmentIconId(p.department)}`} />
  </svg>
)

export const SearchIcon = (p: {
  width?: string | number
  height?: string | number
  className?: string
}) => (
  <svg aria-hidden="true" width={p.width || undefined} height={p.height || undefined} className={p.className}>
    <use xlinkHref="#icon--SG-zoeken" />
  </svg>
)

// ----- Centra

export const getCentrumLogoId = (centrum: SpaarneCentrumType): SpaarneCentrumLogoType => {
  switch (centrum) {
    case 'Asherman expertisecentrum':
      return 'asherman'
    case 'Baria Nederland':
      return 'baria'
    case 'Bekkenbodem centrum':
      return 'bekkenbodem'
    case 'Borstzorg centrum':
      return 'borstzorg'
    case 'centrum Ouderengeneeskunde':
      return 'ouderengeneeskunde'
    case 'Geheugen centrum':
      return 'geheugen'
    case 'Oncologie centrum':
      return 'oncologie'
    case 'Orthopedie centrum':
      return 'orthopedie'
    case 'Thuismonitoring centrum':
      return 'thuismonitoring'
    case 'Trauma centrum':
      return 'trauma'
    case 'Vrouw & Kind centrum':
      return 'vrouw-kind'
  }
}

export const PageHeaderNavLogo = (p: {
  centrum?: SpaarneCentrumType
  className?: string
}) => {
  const vriendenLogo = !p.centrum
  const imageUrls = Map<ImageSize, string>([
    ['48h', vriendenLogo ? '/images/logo--SG--Vrienden.png' : `/images/centra/logo--SG-${getCentrumLogoId(p.centrum!)}-48h.png`],
    ['96h', vriendenLogo ? '/images/logo--SG--Vrienden.png' : `/images/centra/logo--SG-${getCentrumLogoId(p.centrum!)}-96h.png`]
  ])
  return <Image
    urls={imageUrls}
    src={pageHeaderLogoSrc}
    srcset={pageHeaderLogoSrcSet}
    sizes='100vw'
    alt="[*] Spaarne Centrum logo"
    width="260"
    height="48"
    lazyload={true}
    classNames={{ image: p.className || '' }}
  />
}

export type SpaarneDepartmentType = (typeof spaarneDepartments)[number]
export type SpaarneIconType = (typeof spaarneIcons)[number]
export type SpaarneCentrumType = (typeof spaarneCentra)[number]
export type SpaarneCentrumLogoType = (typeof spaarneCentraLogos)[number]

// These names must match exactly with the `afdelingen` list in SP
// https://spaarnegh.sharepoint.com/sites/SpaarneGasthuis_production-v01/Lists/afdelingen/AllItems.aspx
export const spaarneDepartments = [
  'Algemene opname afdeling (AOA)',
  'Anesthesiologie',
  'Apotheek',
  'Baria Nederland (obesitas chirurgie)',
  'Bekkenbodem centrum',
  'Bloedafname (Atalmedial)',
  'Cardiologie',
  'Centrum Seksueel Geweld',
  'Chirurgie',
  'Dermatologie',
  'Dialyse',
  'Diëtetiek',
  'Eerste hartlonghulp',
  'Ergotherapie',
  'Fysiotherapie',
  'Geestelijke verzorging',
  'Geriatrie',
  'Gipspolikliniek',
  'Gynaecologie',
  'Infectiepreventie',
  'Intensive care',
  'Interne geneeskunde',
  'Keel-, neus- en oorheelkunde (KNO)',
  'Kindergeneeskunde',
  'Klinische Neurofysiologie (KNF)',
  'Liesbreukpolikliniek',
  'Logopedie',
  'Longgeneeskunde',
  'Maag-, darm- en leverziekten (MDL)',
  'Maatschappelijk werk',
  'Mammapoli',
  'Mondziekten, Kaak- en Aangezichtschirurgie (MKA-chirurgie)',
  'Neonatologie',
  'Neurochirurgie',
  'Neurologie',
  'Nucleaire geneeskunde',
  'Oncologie',
  'Oogheelkunde',
  'Orthopedie',
  'Palliatieve zorg',
  'Pathologie',
  'Pijnbestrijding',
  'Plastische chirurgie',
  'Psychiatrie en medische psychologie',
  'Radiologie',
  'Reizigersvaccinatie',
  'Reumatologie',
  'Revalidatiegeneeskunde',
  'Spoedeisende Hulp (SEH)',
  'Urologie',
  'Vaatlaboratorium',
  'Verloskunde',
  'Vrouw en Kind',
  'Wonddiagnosecentrum',
] as const

export const spaarneIcons = [
  'afdelingen-en-centra',
  'afdrukken',
  'afnamelab',
  'afspraak-maken',
  'allergologie',
  'aoa',
  'anesthesiologie',
  'apotheek',
  'artsen',
  'baria-nederland',
  'bekkenbodem',
  'berichten',
  'bezoektijden',
  'bloedafname',
  'cardiologie',
  'chirurgie',
  'contact',
  'dermatologie',
  'dialyse',
  'dietetiek',
  'eerste-hartlonghulp',
  'folders',
  'geest-verzorging',
  'geriatrie',
  'gynaecologie',
  'home',
  'hulpknop',
  'informatie',
  'inlog-mijnspaarne',
  'instellingen',
  'intensive-care',
  'interne-geneeskunde',
  'kindgeneeskunde',
  'kno',
  'longziekten',
  'maag-darm',
  'maatschappelijk-werk',
  'medisch-dossier',
  'medische-psychologie',
  'mijn-gegevens',
  'mond-kaak-en-aangezicht',
  'neonatologie',
  'neurologie',
  'nucleaire-geneeskunde',
  'oncologie',
  'oogheelkunde',
  'orthopedie',
  'palliatieve-zorg',
  'paramedische-dienst',
  'pathologie',
  'pijnbestrijding',
  'plastische-chirurgie',
  'radiologie',
  'reizigerspoli',
  'reumatologie',
  'revalidatie',
  'slechtziend',
  'spoedeisende-hulp',
  'uitslagen',
  'urologie',
  'vragenlijst',
  'vrouw-en-kind-centrum',
  'wachttijden',
  'zoeken',
] as const

// These names must match exactly with the `centra` list in SP
// https://spaarnegh.sharepoint.com/sites/SpaarneGasthuis_production-v01/Lists/centra/AllItems.aspx
export const spaarneCentra = [
  'Asherman expertisecentrum',
  'Baria Nederland',
  'Bekkenbodem centrum',
  'Borstzorg centrum',
  'centrum Ouderengeneeskunde',
  'Geheugen centrum',
  'Oncologie centrum',
  'Orthopedie centrum',
  'Thuismonitoring centrum',
  'Trauma centrum',
  'Vrouw & Kind centrum'
] as const

export const spaarneCentraLogos = [
  'asherman',
  'baria',
  'bekkenbodem',
  'borstzorg',
  'geheugen',
  'oncologie',
  'orthopedie',
  'ouderengeneeskunde',
  'thuismonitoring',
  'trauma',
  'vrouw-kind'
] as const

export const DecorativeIcon = (p: {
  iconId: string
  width?: string | number
  height?: string | number
  className?: string
}) => (
  <svg aria-hidden="true" focusable="false" width={p.width || undefined} height={p.height || undefined} className={p.className}>
    <use xlinkHref={`#${p.iconId}`} />
  </svg>
)
